import React, { useState, useLayoutEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import AdsAPIs from "../../../apiRoutes/Ads";
import AgencyAPIs from "../../../apiRoutes/Agency";
import { GET_AGENCIES_QUERY_KEY, GET_AD_TYPES_QUERY_KEY, GET_AD_ADTYPES_QUERY_KEY, GET_RATE_CARDS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import toast from "react-hot-toast";
import useZustandStore from "../../../zustandStore/useZustandStore";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../../utils/constants/permissions";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../../utils/agencyScope";

const CREATE_NEW_VALUE = "__create_new__";

const AddAdTypeAndRateCardModal = ({ open, onClose, agencyScoped: agencyScopedProp, scopedAgencyId: scopedAgencyIdProp }) => {
  const [step, setStep] = useState(1);
  const [agencyId, setAgencyId] = useState("");
  const [adTypeChoice, setAdTypeChoice] = useState("existing");
  const [adtypeId, setAdtypeId] = useState("");
  const [newAdTypeName, setNewAdTypeName] = useState("");
  const [pricePerUnit, setPricePerUnit] = useState("");
  const [currency, setCurrency] = useState("INR");

  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const { hasPermission } = usePermissions();
  const canListAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);
  const agencyScoped = agencyScopedProp ?? isAgencyScopedUser(currentUser);
  const scopedAgencyId = scopedAgencyIdProp ?? getAgencyIdFromUser(currentUser);

  useLayoutEffect(() => {
    if (!open) return;
    if (agencyScoped && scopedAgencyId != null) {
      setAgencyId(String(scopedAgencyId));
      setStep(2);
    } else if (!agencyScoped) {
      setStep(1);
    }
  }, [open, agencyScoped, scopedAgencyId]);

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: 200, offset: 0 }),
    enabled: open && !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const { data: adtypesResult } = useQuery({
    queryKey: GET_AD_TYPES_QUERY_KEY,
    queryFn: () => AdsAPIs.listAdtypes({ axiosPrivate, limit: 200, offset: 0 }),
    enabled: open && step === 2,
  });
  const adtypes = adtypesResult?.data ?? [];

  const createAdtypeMutation = useMutation({
    mutationFn: (payload) => AdsAPIs.createAdtype({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_AD_TYPES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_ADTYPES_QUERY_KEY });
    },
    onError: (e) => toast.error(e?.response?.data?.error_message || e?.message),
  });

  const createRateCardMutation = useMutation({
    mutationFn: (payload) => AdsAPIs.createRateCard({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_RATE_CARDS_QUERY_KEY });
      toast.success("Rate card created.");
      resetAndClose();
    },
    onError: (e) => toast.error(e?.response?.data?.error_message || e?.message),
  });

  const resetAndClose = () => {
    setStep(1);
    setAgencyId("");
    setAdTypeChoice("existing");
    setAdtypeId("");
    setNewAdTypeName("");
    setPricePerUnit("");
    setCurrency("INR");
    onClose();
  };

  const handleNext = (e) => {
    e?.preventDefault?.();
    if (!canListAgencies) {
      toast.error("Agency management permission is required to continue.");
      return;
    }
    if (!agencyId || agencyId === "") {
      toast.error("Select an agency.");
      return;
    }
    setStep(2);
  };

  const handleBack = () => {
    if (agencyScoped) {
      resetAndClose();
    } else {
      setStep(1);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const useNewAdType = adTypeChoice === "new" || adtypeId === CREATE_NEW_VALUE;
    const nameTrimmed = (newAdTypeName || "").trim();

    if (useNewAdType) {
      if (!nameTrimmed) {
        toast.error("Enter a name for the new ad type.");
        return;
      }
    } else {
      if (!adtypeId || adtypeId === "") {
        toast.error("Select an ad type.");
        return;
      }
    }

    let resolvedAdtypeId = adtypeId;
    if (useNewAdType) {
      try {
        const created = await createAdtypeMutation.mutateAsync({ name: nameTrimmed });
        const data = created?.data ?? created;
        resolvedAdtypeId = data?.id;
        if (resolvedAdtypeId == null) {
          toast.error("Ad type was created but could not get ID.");
          return;
        }
        toast.success("Ad type added.");
      } catch {
        return;
      }
    }

    const payload = {
      agency_id: Number(agencyId),
      adtype_id: Number(resolvedAdtypeId),
      price_per_unit: pricePerUnit ? String(pricePerUnit).trim() : null,
      currency: (currency || "").trim() || "INR",
    };
    createRateCardMutation.mutate(payload);
  };

  const isPending = createAdtypeMutation.isPending || createRateCardMutation.isPending;

  return (
    <CustomModal
      open={open}
      title={step === 1 ? "Select agency" : "Ad type & rate card"}
      onClose={resetAndClose}
      handleConfirmation={step === 1 ? handleNext : handleSubmit}
      handleConfirmationBtnText={step === 1 ? "Next" : "Create"}
      isLoading={isPending}
      isButtonsRequired={true}
    >
      <form onSubmit={step === 1 ? handleNext : handleSubmit} className="space-y-4 py-2">
        {step === 1 && (
          <div className="space-y-2">
            {canListAgencies ? (
              <>
                <Label>Agency *</Label>
                <Select value={agencyId} onValueChange={setAgencyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select agency" />
                  </SelectTrigger>
                  <SelectContent>
                    {agencies.map((a, i) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {agencyDisplayName(a, i)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            ) : (
              <p className="text-sm text-muted-foreground py-1">
                Picking an agency for a new rate card requires <span className="font-medium text-foreground">agency management</span> permission.
              </p>
            )}
          </div>
        )}

        {step === 2 && (
          <>
            <div className="flex justify-end">
              <button type="button" onClick={handleBack} disabled={isPending} className="text-sm text-gray-600 hover:underline">
                Back
              </button>
            </div>
            <div className="space-y-2">
              <Label>Ad type *</Label>
              <Select
                value={adtypeId === CREATE_NEW_VALUE ? CREATE_NEW_VALUE : adtypeId}
                onValueChange={(v) => {
                  setAdtypeId(v);
                  setAdTypeChoice(v === CREATE_NEW_VALUE ? "new" : "existing");
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select ad type or create new" />
                </SelectTrigger>
                <SelectContent>
                  {adtypes.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={CREATE_NEW_VALUE}>+ Create new ad type</SelectItem>
                </SelectContent>
              </Select>
              {adtypeId === CREATE_NEW_VALUE && (
                <Input
                  placeholder="New ad type name"
                  value={newAdTypeName}
                  onChange={(e) => setNewAdTypeName(e.target.value)}
                  className="mt-2"
                />
              )}
            </div>
            <div className="space-y-2">
              <Label>Price per unit</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="e.g. 100.50"
                value={pricePerUnit}
                onChange={(e) => setPricePerUnit(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Input
                placeholder="e.g. INR"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>
          </>
        )}
      </form>
    </CustomModal>
  );
};

export default AddAdTypeAndRateCardModal;
